/**
 * Age in whole years. Computed, never stored: a stored age is wrong the day
 * after it is written, which is how the original spreadsheet ended up
 * full of #REF! values.
 *
 * All arithmetic uses UTC components so the result does not shift with the
 * viewer's timezone.
 */
export function computeAge(dob, now = new Date()) {
    const birth = typeof dob === 'string' ? new Date(`${dob.slice(0, 10)}T00:00:00Z`) : dob;
    if (Number.isNaN(birth.getTime()))
        return Number.NaN;
    let age = now.getUTCFullYear() - birth.getUTCFullYear();
    const monthDelta = now.getUTCMonth() - birth.getUTCMonth();
    if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < birth.getUTCDate())) {
        age -= 1;
    }
    return age;
}
/**
 * `now` shifted back by `years`, clamped to the last day of the target month.
 *
 * Naively doing `Date.UTC(now.getUTCFullYear() - years, month, day)` overflows when `now`
 * is 29 February and the target year is not a leap year: JS normalises day 29 of a 28-day
 * February forward into March, silently shifting the boundary by one to two days. Clamping
 * the day to the target month's actual last day keeps this in lockstep with `computeAge`,
 * which treats a 29 February birthday as "not yet occurred" until 1 March in a non-leap year.
 *
 * Property that matters here (and that the tests pin): `computeAge(shiftYearsUtc(now, k), now) === k`
 * for every `k`, in both the ordinary case and the leap-day-clamped case.
 */
function shiftYearsUtc(now, years) {
    const year = now.getUTCFullYear() - years;
    const month = now.getUTCMonth();
    const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(now.getUTCDate(), lastDayOfMonth)));
}
/** Date of birth bounds for an inclusive age range, used to keep filters index-friendly. */
export function ageRangeToDobRange(minAge, maxAge, now = new Date()) {
    const iso = (d) => d.toISOString().slice(0, 10);
    // Someone at most maxAge was born on or after this date: the day after the date that
    // would make them exactly maxAge + 1.
    const from = maxAge === null ? null : (() => {
        const boundary = shiftYearsUtc(now, maxAge + 1);
        boundary.setUTCDate(boundary.getUTCDate() + 1);
        return iso(boundary);
    })();
    // Someone at least minAge was born on or before this date: the date that makes them
    // exactly minAge.
    const to = minAge === null ? null : iso(shiftYearsUtc(now, minAge));
    return { from, to };
}
//# sourceMappingURL=age.js.map